import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBackfillTask, newIdempotencyKey, familyKeys } from '@kidshome/api-contract';

/**
 * `backfill-task` — the kind "Forgot to check this off? Log it for that day" action (ADR-0040). A
 * `missed` (or planned-but-past, within the ~7-day window) occurrence is logged FOR its real scheduled
 * day: the server enforces the window and routes a guardian → `approved` (coins land) and a child's
 * request → `pending_approval` (a grown-up looks first — kids never self-credit). The credit is keyed
 * on `occurrenceId` server-side, so a replay is a no-op (the points ledger stays append-only).
 *
 * Unlike `complete`/`redo` this does NOT optimistically flip the row — the routed result (approved vs
 * pending, with the bounded-window check) is the server's call, so we simply mutate and, on settle,
 * reconcile the day + the single-occurrence detail + the assignee's balance from the source of truth.
 * The consuming component surfaces the kind result + any window-expired message.
 */
export interface BackfillTaskContext {
  /** The occurrence's own scheduled day (for the day-list cache to reconcile). */
  readonly date: string;
  readonly assigneeId: string;
  /** The child whose coin balance a guardian-approved backfill credits (defaults to the assignee). */
  readonly childId?: string;
}

interface Snapshot {
  /** The date/assignee/childId THIS mutation was issued for — pinned in `onMutate` so `onSettled`
   * reconciles the right caches even if the caller (driven by `ChoreDetailPage`'s `occurrenceId` route
   * param) has since re-rendered for a different occurrence while the request was still in flight. */
  date: string;
  assigneeId: string;
  childId: string;
}

export function useBackfillTaskAction(ctx: BackfillTaskContext): {
  /** Log the occurrence for its scheduled day; pass `photoProofKey` for a photo-proof chore. */
  backfill: (occurrenceId: string, photoProofKey?: string) => void;
  /** True while the backfill is in flight. */
  isPending: boolean;
  /** True once the last backfill succeeded (drives the kind result message). */
  isDone: boolean;
  /** The last backfill failure (so the caller can surface it kindly), or null. Cleared on the next try. */
  error: unknown;
  /** Clear the result/error state (e.g. when closing the sheet). */
  reset: () => void;
} {
  const qc = useQueryClient();
  const childId = ctx.childId ?? ctx.assigneeId;

  const mutation = useBackfillTask<Snapshot>({
    onMutate: () => ({ date: ctx.date, assigneeId: ctx.assigneeId, childId }),
    onSettled: (_data, _err, { occurrenceId }, context) => {
      // Reconcile the day/assignee/child THIS mutation was actually issued for — read from the pinned
      // `context` (captured by `onMutate` at mutate-time), NOT the outer-scope `ctx`/`childId` closures,
      // which TanStack Query silently rebinds to the LATEST render's props on every render (incl. while
      // this mutation is still in flight — mirrors the same hazard `complete-task`/`redo-task` guard
      // against). Without this, navigating to a different chore before the request settles would
      // reconcile the WRONG occurrence's day/balance/week.
      const dateToInvalidate = context?.date ?? ctx.date;
      const assigneeIdToInvalidate = context?.assigneeId ?? ctx.assigneeId;
      const childIdToInvalidate = context?.childId ?? childId;
      // The server owns the routed result (approved vs pending) + the bounded-window check, so
      // reconcile every surface that reads the occurrence from the source of truth.
      void qc.invalidateQueries({
        queryKey: familyKeys.day(assigneeIdToInvalidate, dateToInvalidate),
      });
      void qc.invalidateQueries({ queryKey: familyKeys.occurrence(occurrenceId) });
      void qc.invalidateQueries({ queryKey: familyKeys.balance(childIdToInvalidate) });
      // Logging a forgotten day changes that day's cell on the calendar, so reconcile the week agenda
      // for this assignee too (the week query isn't covered by the day key above).
      void qc.invalidateQueries({ queryKey: familyKeys.weeks(assigneeIdToInvalidate) });
      // A backfilled chore that auto-approves advances the shared cooperative quest bar too.
      void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
    },
  });

  const backfill = useCallback(
    (occurrenceId: string, photoProofKey?: string) => {
      mutation.reset();
      // The `.../backfill` route is `@Idempotent`, so carry a fresh `Idempotency-Key` — without it the
      // server rejects the write 400 ("Idempotency-Key header is required for this operation").
      mutation.mutate({ occurrenceId, photoProofKey, idempotencyKey: newIdempotencyKey() });
    },
    [mutation],
  );

  return {
    backfill,
    isPending: mutation.isPending,
    isDone: mutation.isSuccess,
    error: mutation.error,
    reset: () => mutation.reset(),
  };
}
